import { ChangeDetectionStrategy, Component, effect, ElementRef, viewChild } from '@angular/core';
import { SectionComponent } from '../../components/section/section.component';
import { Section, SectionAttributes } from '../../types/section';
import { debounceTime, distinctUntilChanged, filter, fromEvent, map, of, switchMap } from 'rxjs';
import { AsyncPipe, isPlatformServer } from '@angular/common';
import { SafePipe } from '../../providers/safe.pipe';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';

type IframeSandboxPermissions = 'allow-downloads' | 'allow-forms' | 'allow-modals' | 'allow-orientation-lock' | 'allow-pointer-lock' | 'allow-popups' | 'allow-popups-to-escape-sandbox' | 'allow-presentation' | 'allow-same-origin' | 'allow-scripts' | 'allow-top-navigation' | 'allow-top-navigation-by-user-activation' | 'allow-top-navigation-to-custom-protocols';

type IframeSection = Section<
  {
    src: string;
    initialHeight: number;
    sandbox: IframeSandboxPermissions[];
    numColumns: 4 | 8 | 12;
  } & SectionAttributes,
  [
    'src',
    'numColumns',
  ],
  'iframe'
>;

/**
 * // This simple script can be used to inform the parent window about the height of the iframe content.
 *
 * function sendContentSizeToParent() {
 *   const body = document.body;
 *   const html = document.documentElement;
 *
 *   const height = Math.max(
 *       body.scrollHeight, body.offsetHeight, html.offsetHeight
 *   );
 *
 *   const message = { message: 'content-size', height };
 *   parent.postMessage(message, '*');
 * }
 *
 * if (parent) {
 *   window.addEventListener('load', sendContentSizeToParent);
 *   window.addEventListener('resize', sendContentSizeToParent);
 * }
 */

@Component({
  selector: 'app-iframe',
  standalone: true,
  imports: [
    AsyncPipe,
    SafePipe,
    FsButtonComponent,
],
  templateUrl: './iframe.component.html',
  styleUrl: './iframe.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IframeComponent extends SectionComponent<IframeSection> {

  private iframeElement = viewChild.required<ElementRef<HTMLIFrameElement>>('iframeElement');

  public constructor() {
    super();
    effect(() => {
      // This is needed because of NG0910
      this.iframeElement().nativeElement.setAttribute('sandbox', this.attributes.sandbox.join(' '));
    })
  }

  protected iframeHeight$ = of(isPlatformServer(this.platformId)).pipe(
    switchMap((isServer) => {
      if (isServer) {
        return of(null);
      }

      return fromEvent<MessageEvent>(window, 'message').pipe(
        map((event) => {
          if (event.source !== this.iframeElement().nativeElement.contentWindow) {
            return;
          }

          if (!event.data) {
            return;
          }

          if (event.data.message !== 'content-size') {
            return;
          }

          return event.data.height + 1; // +1 prevents scrollbars from floating point errors
        }),
        filter((height) => height !== undefined),
        distinctUntilChanged(),
        debounceTime(100),
      );
    }),
  );

}
